import { useState, useMemo, useEffect, useCallback, useRef } from "react";

import {
  Bell,
  Briefcase,
  Building2,
  // Flag, // Today tab temporarily hidden
  LayoutList,
  Map as MapIcon,
  Settings as SettingsIcon,
  Target,
  Users,
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AccountPrioritiesTab from "./AccountPrioritiesTab";
import ActionsTab from "./ActionsTab";
import PortfolioGoalsTab from "./PortfolioGoalsTab";
import PortfolioMapTab, { LENS_KEY_TERMS } from "./PortfolioMapTab";
import PortfolioSettingsModal from "./PortfolioSettingsModal";
import PortfolioSummaryStrip, {
  type TileFilter,
} from "./PortfolioSummaryStrip";
import RelationshipPrioritiesTab from "./RelationshipPrioritiesTab";
import { usePortfolioMapLenses } from "./usePortfolioMapLenses";
import {
  SIGNALS_OFF_MESSAGE,
  useSignalPresentation,
} from "./useSignalPresentation";
import "../Relationship/account-relationships/relationship-prototype-skin.css";
// Today UI temporarily hidden — keep imports commented for easy restore.
// import TodayOverlayPanel from "./TodayOverlayPanel";
// import TodayTab from "./TodayTab";
import PageHeader from "../../Components/PageHeader";
import TabsComponent from "../../Components/Tab";
import PortfolioAssessChoiceModal, {
  PortfolioAssessChoice,
} from "../../Container/AssessmentsContainer/PortfolioAssessChoiceModal";
import ReviewAssessmentModal, {
  AssessmentResponseMap,
  TemplateData,
} from "../../Container/AssessmentsContainer/ReviewAssessmentModal";
import useSpeechToText from "../../Hooks/useSpeechToText";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import {
  createSelfAssessment,
  CreateSelfAssessmentPayload,
  fetchAssessmentTemplates,
  fetchAssessmentValidation,
  fetchSelfAssessmentConfig,
  fetchSelfAssessmentLatest,
  LatestSelfAssessmentContent,
  lookupReviewAssessments,
  lookupSelfAssessments,
  submitReviewAssessment,
} from "../../Store/Reducers/AssessmentSlice";
import {
  fetchPortfolioAccounts,
  type PortfolioAccount,
} from "../../Store/Reducers/CustomerWorkspaceSlice";
import { fetchMyPortfolioOverview } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import { fetchSignalFeed } from "../../Store/Reducers/SignalFeedSlice";
import { fetchRlSettingConfig } from "../../Store/Reducers/VendorAdminSlice";
import type { AppDispatch } from "../../Store/Store";
import { isRole, ROLES } from "../../Utils/Constants/RoleConstants";
import type { TeamPeerBanner } from "../../Utils/Helper/assessmentOriginHelpers";
import {
  findPendingTeamPeerBanner,
  formatTeamAssessmentTitle,
  normalizeTeamAssessmentDisplayName,
  TEAM_ASSESSMENT_DESCRIPTION,
} from "../../Utils/Helper/assessmentOriginHelpers";
import { getLevelDisplayName } from "../../Utils/Helper/levelUtils";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";
import {
  normalizeFanInCap,
  normalizeFanOutCaps,
  normalizeOverallFanOutCap,
  resolveFanOutCap,
} from "../../Utils/Helper/rlFanCaps";
import {
  DEFAULT_CYCLE_CURRENCY_DAYS,
  buildStaleDaysLabel,
  isDraftNewerThanPrior,
  resolveSelfAssessmentCurrencyDays,
} from "../../Utils/Helper/selfAssessmentCurrency";
import { toast } from "../../Utils/Helper/toast";
import {
  formatAssessmentSubmitSavedOnDate,
  formatAssessmentSubmitSuccessDate,
  formatReassessBeforeLabel,
} from "../ReviewAssessments/assessmentSubmitSuccessCopy";
import AssessmentSubmitSuccessModal, {
  type AssessmentSubmitSuccessInfo,
} from "../ReviewAssessments/AssessmentSubmitSuccessModal";
import { getLoggedInExecutiveId } from "../ReviewAssessments/disconnectResolutionUtils";

const buildFormalAssessmentPendingMessage = (count: number): string | null => {
  if (count <= 0) return null;
  if (count === 1) {
    return "Formal assessment (1) is already pending for this account. Complete the formal assessment before using Team Assess.";
  }
  return `Formal assessments (${count}) are already pending for this account. Complete a formal assessment before using Team Assess.`;
};

const buildFormalIncludedMessage = (accountName: string): string =>
  `A Formal assessment is already running on ${accountName || "this account"}. Your input will be included in it.`;

type FormalResumePayload = {
  assessmentId: string;
  assessmentName?: string | null;
  questions?: TemplateData["json_schema"];
  status?: string | null;
  response?: AssessmentResponseMap | null;
  isDraft: boolean;
  /** Answers came from Completed; Assigned answers are only a carried prefill. */
  alreadySubmitted: boolean;
  dueDate?: string | null;
  submittedAt?: string | null;
};

/**
 * Load Formal draft/assigned/completed for Take Formal without touching Redux list state.
 */
const loadFormalResumeForAccount = async (
  dispatch: AppDispatch,
  accountId: string,
  formalId: string,
  executiveId: string
): Promise<FormalResumePayload | null> => {
  if (!accountId || !formalId || !executiveId) return null;

  for (const tab of ["draft", "assigned", "completed"] as const) {
    try {
      const response = (await dispatch(
        lookupReviewAssessments({
          executive_id: executiveId,
          tab,
          account_id: accountId,
          ...(tab === "draft" ? { is_draft: true } : {}),
        })
      ).unwrap()) as {
        assessments?: Array<{
          assessment_id?: string;
          assessment_name?: string | null;
          questions?: TemplateData["json_schema"];
          status?: string | null;
          due_date?: string | null;
          accounts?: Array<{
            account_id?: string | null;
            executives?: Array<{
              executive_id?: string | null;
              status?: string | null;
              completed_on?: string | null;
              updated_at?: string | null;
              response?: AssessmentResponseMap | null;
            }>;
          }>;
        }>;
      };

      const assessments = response?.assessments ?? [];
      const match =
        assessments.find(a => String(a.assessment_id) === formalId) ??
        (assessments.length === 1 ? assessments[0] : null);
      if (!match?.assessment_id) continue;

      const account =
        match.accounts?.find(a => String(a.account_id) === accountId) ??
        match.accounts?.[0];
      const executive =
        account?.executives?.find(
          e => String(e.executive_id) === executiveId
        ) ?? account?.executives?.[0];
      const savedResponse = executive?.response ?? null;
      const hasSavedAnswers =
        Boolean(savedResponse) &&
        Object.keys(savedResponse as object).length > 0;

      return {
        assessmentId: String(match.assessment_id),
        assessmentName: match.assessment_name,
        questions: match.questions,
        status: match.status || executive?.status || null,
        response: hasSavedAnswers ? savedResponse : null,
        isDraft: tab === "draft" && hasSavedAnswers,
        alreadySubmitted: tab === "completed" && hasSavedAnswers,
        dueDate: match.due_date ?? null,
        submittedAt: executive?.updated_at || executive?.completed_on || null,
      };
    } catch {
      // try next tab
    }
  }

  return null;
};

/**
 * Resolve peer banner without touching Redux review-assessment list state
 * (Status / Portfolio Assess must not clobber My Assessments tabs).
 */
const fetchPendingTeamPeerBannerForAccount = async (
  dispatch: AppDispatch,
  accountId: string,
  assessmentId: string | null | undefined,
  executiveId: string
): Promise<TeamPeerBanner | null> => {
  if (!accountId || !executiveId) return null;

  for (const status of ["assigned", "draft"] as const) {
    try {
      const response = (await dispatch(
        lookupSelfAssessments({
          status,
          account_id: accountId,
          page_size: 25,
        })
      ).unwrap()) as {
        content?: {
          assessments?: Array<{
            assessment_id?: string;
            accounts?: Array<{
              account_id?: string;
              executives?: Array<{
                executive_id?: string | null;
                executive_name?: string | null;
                status?: string | null;
                completed_on?: string | null;
                response?: Record<string, string | string[]> | null;
              }>;
            }>;
          }>;
        };
        assessments?: Array<{
          assessment_id?: string;
          accounts?: Array<{
            account_id?: string;
            executives?: Array<{
              executive_id?: string | null;
              executive_name?: string | null;
              status?: string | null;
              completed_on?: string | null;
              response?: Record<string, string | string[]> | null;
            }>;
          }>;
        }>;
      };

      const assessments =
        response?.content?.assessments ?? response?.assessments ?? [];
      if (!assessments.length) continue;

      const match =
        (assessmentId
          ? assessments.find(a => a.assessment_id === assessmentId)
          : null) ?? assessments[0];
      if (!match) continue;

      const account =
        match.accounts?.find(a => a.account_id === accountId) ??
        match.accounts?.[0];
      const banner = findPendingTeamPeerBanner(
        account?.executives,
        executiveId
      );
      if (banner) return banner;
    } catch {
      // Keep trying other statuses / fall through.
    }
  }

  return null;
};

export type PendingFormalValidationAssessment = {
  assessment_id?: string;
  assessment_name?: string;
  assessment_type?: string;
  assessment_status?: string;
  assignment_status?: string;
  assignment_id?: string;
  due_date?: string | null;
  assessment_remaining_date?: string | null;
  disconnect_status?: string | null;
};

type PendingSelfAssessment = {
  assessment_id?: string;
  assessment_name?: string;
  assessment_type?: string;
  assessment_status?: string;
  assignment_status?: string;
  assignment_id?: string;
  due_date?: string | null;
  assessment_remaining_date?: string | null;
  is_stale?: boolean;
  disconnect_status?: string | null;
};

type OpenFormalInfo = {
  assessment_id?: string;
  status?: string;
  user_assigned?: boolean;
  user_included?: boolean;
  user_assignment_status?: string | null;
  due_date?: string | null;
  esl_responses_locked?: boolean;
};

type ActiveDisconnectInfo = {
  status?: string | null;
  assessment_id?: string | null;
  resolution_id?: string | null;
};

type AssessmentValidationContent = {
  account_id?: string;
  user_id?: string;
  has_pending?: boolean;
  pending_count?: number;
  assessments?: PendingFormalValidationAssessment[];
  is_self_assessment?: boolean;
  pending_self_assessment?: PendingSelfAssessment | null;
  open_formal?: OpenFormalInfo | null;
  active_disconnect?: ActiveDisconnectInfo | null;
};

type AssessmentValidationApiResponse = {
  status?: string;
  content?: AssessmentValidationContent;
  response?: { status?: number; message?: string };
};

const getValidationContent = (
  res: AssessmentValidationApiResponse
): AssessmentValidationContent | null => res?.content ?? null;

const getPendingSelfAssessmentId = (
  content: AssessmentValidationContent | null | undefined
): string | null => {
  const id = content?.pending_self_assessment?.assessment_id;
  return id ? String(id) : null;
};

const isSelfAssessmentResumable = (
  meta?: {
    status?: string;
    assessment_status?: string;
    is_stale?: boolean;
  } | null
): boolean => {
  if (!meta) return false;
  const status = (meta.status || meta.assessment_status || "").toUpperCase();
  if (status === "PRE_PUBLISH") return true;
  if (status === "DRAFT") return true;
  if (status === "PRE_ACTIVE" && !meta.is_stale) return true;
  return false;
};

const resolveOpenSelfAssessmentId = (
  validationContent: AssessmentValidationContent | null | undefined,
  latest: LatestSelfAssessmentContent | null | undefined,
  fallbackId?: string,
  freshStart?: boolean
): string | null => {
  const pending = validationContent?.pending_self_assessment;
  if (pending?.assessment_id && isSelfAssessmentResumable(pending)) {
    return String(pending.assessment_id);
  }
  if (fallbackId) return String(fallbackId);
  const latestMeta = latest?.self_assessment;
  const cycleDraftId = latest?.cycle_draft?.assessment_id;
  const latestIsDraft = Boolean(latest?.cycle_draft?.response?.is_draft);
  if (freshStart && !latestIsDraft) return null;
  if (cycleDraftId && latestIsDraft) {
    return String(cycleDraftId);
  }
  if (
    latestMeta?.assessment_id &&
    latestMeta.origin !== "formal" &&
    isSelfAssessmentResumable(latestMeta)
  ) {
    return String(latestMeta.assessment_id);
  }
  return null;
};

/** Formal cycle still open for this account (includes COMPLETED and a partial hub publish). */
const isOpenFormalCycleActive = (status?: string | null): boolean => {
  const normalized = (status || "").trim().toUpperCase();
  return (
    normalized === "ACTIVE" ||
    normalized === "ONGOING" ||
    normalized === "COMPLETED" ||
    normalized === "PUBLISHED"
  );
};

/**
 * Formal still accepts responses on this account.
 * PUBLISHED here is a partial hub publish: the API only returns this row when
 * this account's assignment is still unpublished.
 */
const isFormalStatusTakeable = (status?: string | null): boolean => {
  const normalized = (status || "").trim().toUpperCase();
  return (
    normalized === "ACTIVE" ||
    normalized === "ONGOING" ||
    normalized === "PUBLISHED"
  );
};

export type OpenPortfolioSelfAssessmentResult =
  | "opened"
  | "finalization"
  | "esl_locked"
  | "error";

type PortfolioModalStep = "closed" | "loading" | "choice" | "gateB" | "form";

export type MyAssessmentsNavTarget = {
  tab: "draft" | "assigned";
  kind: "team" | "formal";
};

type AccountAssessmentPresence = {
  selfDraft: boolean;
  selfAssigned: boolean;
  formalDraft: boolean;
  formalAssigned: boolean;
};

const EMPTY_ACCOUNT_PRESENCE: AccountAssessmentPresence = {
  selfDraft: false,
  selfAssigned: false,
  formalDraft: false,
  formalAssigned: false,
};

const isFormalAssignmentDraft = (
  item: PendingFormalValidationAssessment
): boolean => (item.assignment_status || "").toLowerCase() === "in_progress";

/** Prefer draft over assigned; prefer the current taking kind when both exist. */
export const resolveMyAssessmentsNavTarget = (
  presence: AccountAssessmentPresence,
  opts: { isDraftSession: boolean; takingKind: "team" | "formal" }
): MyAssessmentsNavTarget | null => {
  const selfDraft =
    presence.selfDraft || (opts.takingKind === "team" && opts.isDraftSession);
  const formalDraft =
    presence.formalDraft ||
    (opts.takingKind === "formal" && opts.isDraftSession);

  if (selfDraft || formalDraft) {
    if (opts.takingKind === "formal" && formalDraft) {
      return { tab: "draft", kind: "formal" };
    }
    if (opts.takingKind === "team" && selfDraft) {
      return { tab: "draft", kind: "team" };
    }
    if (formalDraft) return { tab: "draft", kind: "formal" };
    return { tab: "draft", kind: "team" };
  }

  if (presence.selfAssigned || presence.formalAssigned) {
    if (opts.takingKind === "formal" && presence.formalAssigned) {
      return { tab: "assigned", kind: "formal" };
    }
    if (opts.takingKind === "team" && presence.selfAssigned) {
      return { tab: "assigned", kind: "team" };
    }
    if (presence.formalAssigned) {
      return { tab: "assigned", kind: "formal" };
    }
    return { tab: "assigned", kind: "team" };
  }

  return null;
};

export type OpenPortfolioSelfAssessmentOptions = {
  fallbackAssessmentId?: string;
  initialResponses?: AssessmentResponseMap;
  /** Status-tab Assess: do not resume an in-memory/latest cycle unless validation reports a pending draft. */
  freshStart?: boolean;
  teamPeerBanner?: TeamPeerBanner | null;
  /** Completed → Re-assess: show submitted-ago / due-date banner. */
  reassessBanner?: {
    submittedAt?: string | null;
    dueDate?: string | null;
  } | null;
};

const mapPriorResponsesToForm = (
  prior?: LatestSelfAssessmentContent["response"]
): AssessmentResponseMap => {
  const r = prior?.responses ?? {};
  const mapped: AssessmentResponseMap = {};
  if (r.maturity_level) mapped.maturity_level = r.maturity_level;
  if (r.rationale) mapped.rationale = r.rationale;
  Object.entries(r).forEach(([key, value]) => {
    if (value !== undefined && mapped[key] === undefined) {
      mapped[key] = value;
    }
  });
  return mapped;
};

type SelfAssessmentTemplate = {
  template_id?: string;
  id?: string;
  name?: string;
  description?: string;
  json_schema?: {
    metadata?: { template_kind?: string };
    questions?: unknown[];
  };
  is_active?: boolean;
};

type PortfolioSubmitAction = "draft" | "submit" | "navigate";

const getTemplateId = (template: SelfAssessmentTemplate): string =>
  String(template.template_id ?? template.id ?? "");

export const resolveSelfAssessmentTemplate = (
  templatesPayload: unknown
): SelfAssessmentTemplate | null => {
  const list = (() => {
    if (!templatesPayload) return [];
    if (Array.isArray(templatesPayload)) return templatesPayload;
    const payload = templatesPayload as {
      templates?: SelfAssessmentTemplate[];
      data?:
        | { templates?: SelfAssessmentTemplate[] }
        | SelfAssessmentTemplate[];
    };
    return (
      payload.templates ??
      (Array.isArray(payload.data) ? payload.data : payload.data?.templates) ??
      []
    );
  })();

  const match = (items: SelfAssessmentTemplate[]) =>
    items.find(
      t => t.json_schema?.metadata?.template_kind === "self_assessment"
    );

  return match(list.filter(t => t.is_active !== false)) ?? match(list) ?? null;
};

const buildCreatePayload = (
  template: SelfAssessmentTemplate,
  accountId: string,
  asDraft = false,
  accountName?: string | null
): CreateSelfAssessmentPayload => ({
  name: formatTeamAssessmentTitle("Team Assessment", accountName),
  description: TEAM_ASSESSMENT_DESCRIPTION,
  account_id: accountId,
  ...(asDraft ? { as_draft: true } : {}),
  data: { questions: template.json_schema?.questions ?? [] },
});

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "string" && error) return error;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message || fallback;
  }
  return fallback;
};

const formatLevelForSuccess = (raw?: string | number | null): string | null => {
  if (raw === null || raw === undefined || raw === "") return null;
  return getLevelDisplayName(raw);
};

export const usePortfolioSelfAssessment = (
  template: SelfAssessmentTemplate | null,
  withOrg: (path: string) => string
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    isCreatingSelfAssessment,
    isSubmittingReviewAssessment,
    selfAssessmentConfig,
  } = useAppSelector(state => state.assessment);

  const orgCurrencyDays = resolveSelfAssessmentCurrencyDays(
    selfAssessmentConfig?.cadence_days,
    DEFAULT_CYCLE_CURRENCY_DAYS
  );

  const [modalStep, setModalStep] = useState<PortfolioModalStep>("closed");
  const [accountId, setAccountId] = useState("");
  const [accountName, setAccountName] = useState("");
  const [templateData, setTemplateData] = useState<TemplateData | null>(null);
  const [selfAssessmentId, setSelfAssessmentId] = useState<string | null>(null);
  const [responses, setResponses] = useState<
    Record<string, AssessmentResponseMap>
  >({});
  const [submitAction, setSubmitAction] =
    useState<PortfolioSubmitAction | null>(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isValidationLoading, setIsValidationLoading] = useState(false);
  const [hasFormalAssessmentPending, setHasFormalAssessmentPending] =
    useState(false);
  const [pendingFormalCount, setPendingFormalCount] = useState(0);
  const [pendingFormalAssessments, setPendingFormalAssessments] = useState<
    PendingFormalValidationAssessment[]
  >([]);
  /** Takeable open Formal that blocks Team Assess (may be missing from validations.assessments). */
  const [blockingOpenFormalId, setBlockingOpenFormalId] = useState<
    string | null
  >(null);
  const [blockingOpenFormalStatus, setBlockingOpenFormalStatus] = useState<
    string | null
  >(null);
  const [blockingOpenFormalUnassigned, setBlockingOpenFormalUnassigned] =
    useState(false);
  const [isTakeFormalLoading, setIsTakeFormalLoading] = useState(false);
  const [latestSelfData, setLatestSelfData] =
    useState<LatestSelfAssessmentContent | null>(null);
  const [assessMode, setAssessMode] = useState<PortfolioAssessChoice | null>(
    null
  );
  const [carryForwardNote, setCarryForwardNote] = useState("");
  const [priorResponseId, setPriorResponseId] = useState<string | null>(null);
  const [portfolioStatusBadge, setPortfolioStatusBadge] = useState<
    string | null
  >(null);
  const [submitSuccessInfo, setSubmitSuccessInfo] =
    useState<AssessmentSubmitSuccessInfo | null>(null);
  const closeSuccessModal = useCallback(() => setSubmitSuccessInfo(null), []);
  const [gateBOptions, setGateBOptions] = useState<{
    formalAssessmentId: string;
    selfTemplateId: string;
  } | null>(null);
  const [isFormalFoldIn, setIsFormalFoldIn] = useState(false);
  /** Current taking path — drives Go to My Assessments kind preference. */
  const [takingKind, setTakingKind] = useState<"team" | "formal">("team");
  /** True when resuming or after saving a draft — drives Go to My Assessments. */
  const [isDraftSession, setIsDraftSession] = useState(false);
  const [forceReadOnly, setForceReadOnly] = useState(false);
  const [accountPresence, setAccountPresence] =
    useState<AccountAssessmentPresence>(EMPTY_ACCOUNT_PRESENCE);
  const [teamPeerBanner, setTeamPeerBanner] = useState<TeamPeerBanner | null>(
    null
  );
  const [privateRemainder, setPrivateRemainder] = useState<{
    countsOn: string | null;
  } | null>(null);
  const minePortfolioAccounts = useAppSelector(
    state => state.customerWorkspace.portfolioAccounts.items
  );
  const [reassessBanner, setReassessBanner] = useState<{
    submittedAt?: string | null;
    dueDate?: string | null;
  } | null>(null);

  const showChoiceModal = modalStep === "loading" || modalStep === "choice";
  const showGateBModal = modalStep === "gateB";
  const showFormModal = modalStep === "form";
  const portfolioFormFlow: "default" | "continued" = assessMode
    ? "continued"
    : "default";

  const { speechSupported, isListening, startListening, stopListening } =
    useSpeechToText();

  const responseKey = selfAssessmentId ?? templateData?.id ?? "";
  const isSubmitting =
    isCreatingSelfAssessment ||
    isSubmittingReviewAssessment ||
    submitAction !== null;

  const resetModal = useCallback(() => {
    setModalStep("closed");
    setTemplateData(null);
    setSelfAssessmentId(null);
    setSubmitAction(null);
    setQuestionIndex(0);
    setAccountId("");
    setAccountName("");
    setVoiceEnabled(false);
    setIsValidationLoading(false);
    setHasFormalAssessmentPending(false);
    setPendingFormalCount(0);
    setPendingFormalAssessments([]);
    setBlockingOpenFormalId(null);
    setBlockingOpenFormalStatus(null);
    setBlockingOpenFormalUnassigned(false);
    setIsTakeFormalLoading(false);
    setLatestSelfData(null);
    setAssessMode(null);
    setCarryForwardNote("");
    setPriorResponseId(null);
    setPortfolioStatusBadge(null);
    setGateBOptions(null);
    setIsFormalFoldIn(false);
    setTakingKind("team");
    setIsDraftSession(false);
    setForceReadOnly(false);
    setAccountPresence(EMPTY_ACCOUNT_PRESENCE);
    setTeamPeerBanner(null);
    setReassessBanner(null);
    setPrivateRemainder(null);
    setResponses({});
    stopListening();
  }, [stopListening]);

  const adoptAssessmentId = useCallback(
    (assessmentId: string): string => {
      if (!template) return assessmentId;
      const templateId = getTemplateId(template);

      setSelfAssessmentId(assessmentId);
      setTemplateData(prev => (prev ? { ...prev, id: assessmentId } : null));

      if (templateId && templateId !== assessmentId) {
        setResponses(prev => {
          const saved = prev[templateId] ?? prev[assessmentId];
          if (!saved) return prev;
          const { [templateId]: _, ...rest } = prev;
          return {
            ...rest,
            [assessmentId]: { ...(rest[assessmentId] || {}), ...saved },
          };
        });
      }

      return assessmentId;
    },
    [template]
  );

  const ensureCreated = useCallback(
    async (asDraft = false): Promise<string | null> => {
      if (!template || !accountId) return null;
      const templateId = getTemplateId(template);
      if (selfAssessmentId) return selfAssessmentId;

      const resolvedFromTemplate =
        templateData?.id && templateId && templateData.id !== templateId
          ? templateData.id
          : null;
      if (resolvedFromTemplate) return adoptAssessmentId(resolvedFromTemplate);

      try {
        const validationRes = (await dispatch(
          fetchAssessmentValidation(accountId)
        ).unwrap()) as AssessmentValidationApiResponse;

        const content = getValidationContent(validationRes);
        const pendingId = getPendingSelfAssessmentId(content);
        if (pendingId) return adoptAssessmentId(pendingId);
      } catch {
        // No validation result
      }

      try {
        const created = await dispatch(
          createSelfAssessment(
            buildCreatePayload(template, accountId, asDraft, accountName)
          )
        ).unwrap();
        const assessmentId = String(created.assessment_id ?? created.id ?? "");
        if (!assessmentId) {
          toast.error("Team assessment was created without an id");
          return null;
        }
        return adoptAssessmentId(assessmentId);
      } catch (error) {
        toast.error(getErrorMessage(error, "Failed to create team assessment"));
        return null;
      }
    },
    [
      selfAssessmentId,
      template,
      templateData?.id,
      accountId,
      accountName,
      dispatch,
      adoptAssessmentId,
    ]
  );

  const saveAssessment = useCallback(
    async (
      isDraft: boolean,
      action: PortfolioSubmitAction,
      closeOnSuccess = false
    ) => {
      if (!templateData || !accountId || hasFormalAssessmentPending)
        return false;

      setSubmitAction(action);
      try {
        const assessmentId = await ensureCreated(isDraft);
        if (!assessmentId) return false;

        const raw = responses[assessmentId] ?? responses[templateData.id] ?? {};
        const payload = Object.fromEntries(
          Object.entries(raw).filter(([, v]) => v !== undefined)
        ) as Record<string, string | string[]>;

        const submitResult = await dispatch(
          submitReviewAssessment({
            assessment_id: assessmentId,
            account_id: accountId,
            responses: payload,
            is_draft: isDraft,
            ...(assessMode === "carry_forward" && priorResponseId
              ? {
                  carry_forward: true,
                  prior_response_id: priorResponseId,
                  ...(carryForwardNote.trim()
                    ? { carry_forward_note: carryForwardNote.trim() }
                    : {}),
                }
              : {}),
          })
        ).unwrap();

        const resolvedAssessmentId = String(
          submitResult?.assessment_id ??
            submitResult?.data?.assessment_id ??
            assessmentId
        );
        if (resolvedAssessmentId !== assessmentId) {
          adoptAssessmentId(resolvedAssessmentId);
        }
        if (isDraft) {
          setIsDraftSession(true);
          setAccountPresence(prev =>
            takingKind === "formal"
              ? { ...prev, formalDraft: true }
              : { ...prev, selfDraft: true }
          );
          toast.success("Draft saved successfully");
        } else {
          const levelLabel = formatLevelForSuccess(
            payload.maturity_level as string | undefined
          );
          const matchingFormal = pendingFormalAssessments.find(
            item => String(item.assessment_id) === String(resolvedAssessmentId)
          );
          const dueRaw =
            reassessBanner?.dueDate ||
            latestSelfData?.self_assessment?.due_date ||
            latestSelfData?.self_assessment?.assessment_remaining_date ||
            matchingFormal?.due_date ||
            matchingFormal?.assessment_remaining_date ||
            null;
          setSubmitSuccessInfo({
            kind: takingKind === "formal" ? "formal" : "team",
            accountId,
            accountName,
            levelLabel,
            dateLabel: formatAssessmentSubmitSavedOnDate(),
            dueDateLabel: formatAssessmentSubmitSuccessDate(dueRaw),
            remainderCountsOnLabel: formatAssessmentSubmitSuccessDate(
              submitResult?.remainder_counts_on
            ),
            inputDisposition: submitResult?.input_disposition ?? null,
            reassessBeforeLabel: formatReassessBeforeLabel(
              submitResult?.round_due_date
            ),
          });
        }

        if (closeOnSuccess) resetModal();
        return true;
      } catch (error) {
        toast.error(
          getErrorMessage(
            error,
            isDraft ? "Failed to save draft" : "Failed to submit assessment"
          )
        );
        return false;
      } finally {
        setSubmitAction(null);
      }
    },
    [
      templateData,
      accountId,
      accountName,
      hasFormalAssessmentPending,
      ensureCreated,
      adoptAssessmentId,
      responses,
      assessMode,
      priorResponseId,
      carryForwardNote,
      dispatch,
      resetModal,
      takingKind,
      reassessBanner?.dueDate,
      latestSelfData,
      pendingFormalAssessments,
    ]
  );

  const applyPriorResponsePrefill = useCallback(
    (templateId: string, latest: LatestSelfAssessmentContent) => {
      const prior = latest.response;
      const prefill = mapPriorResponsesToForm(prior);
      if (!Object.keys(prefill).length) return;

      setResponses(prev => ({
        ...prev,
        [templateId]: { ...(prev[templateId] || {}), ...prefill },
      }));
      setPriorResponseId(prior?.response_id ?? null);
      setPortfolioStatusBadge(buildStaleDaysLabel(latest));
    },
    []
  );

  const openForAccount = useCallback(
    async (
      record: { id: string; name: string },
      options?: OpenPortfolioSelfAssessmentOptions
    ): Promise<OpenPortfolioSelfAssessmentResult> => {
      if (!template) {
        toast.error(
          "No team-assessment template is configured. Please contact your administrator."
        );
        return "error";
      }

      const templateId = getTemplateId(template);
      if (!templateId) {
        toast.error("Team assessment template is missing an identifier.");
        return "error";
      }

      setHasFormalAssessmentPending(false);
      setPendingFormalCount(0);
      setPendingFormalAssessments([]);
      setBlockingOpenFormalId(null);
      setBlockingOpenFormalStatus(null);
      setBlockingOpenFormalUnassigned(false);
      setIsTakeFormalLoading(false);
      setAssessMode(null);
      setCarryForwardNote("");
      setPriorResponseId(null);
      setPortfolioStatusBadge(null);
      setLatestSelfData(null);
      setIsFormalFoldIn(false);
      setTakingKind("team");
      setIsDraftSession(false);
      setForceReadOnly(false);
      setAccountPresence(EMPTY_ACCOUNT_PRESENCE);
      setTeamPeerBanner(options?.teamPeerBanner ?? null);
      setReassessBanner(options?.reassessBanner ?? null);
      setPrivateRemainder(null);
      setResponses({});
      setIsValidationLoading(true);
      setModalStep("loading");
      setAccountId(record.id);
      setAccountName(record.name);
      setQuestionIndex(0);

      try {
        const validationRes = (await dispatch(
          fetchAssessmentValidation(record.id)
        ).unwrap()) as AssessmentValidationApiResponse;

        if (
          validationRes?.status === "failure" ||
          validationRes?.status === "unauthorized" ||
          validationRes?.response?.status === 401
        ) {
          toast.error("Unable to validate assessment eligibility.");
          resetModal();
          return "error";
        }

        const validationContent = getValidationContent(validationRes);
        const pendingSelfAssessment =
          validationContent?.pending_self_assessment ?? null;
        const formalAssessments =
          validationContent && Array.isArray(validationContent.assessments)
            ? validationContent.assessments
            : [];
        const pendingCount =
          validationContent &&
          typeof validationContent.pending_count === "number"
            ? validationContent.pending_count
            : formalAssessments.length;
        const hasPending =
          Boolean(validationContent?.has_pending) || pendingCount > 0;

        let latestPayload: LatestSelfAssessmentContent | null = null;
        try {
          latestPayload = await dispatch(
            fetchSelfAssessmentLatest(record.id)
          ).unwrap();
        } catch {
          latestPayload = null;
        }

        setLatestSelfData(latestPayload);

        const pendingAssessmentId = resolveOpenSelfAssessmentId(
          validationContent,
          latestPayload,
          options?.fallbackAssessmentId,
          options?.freshStart
        );

        const openFormal = validationContent?.open_formal;
        const openFormalId = openFormal?.assessment_id
          ? String(openFormal.assessment_id)
          : null;
        const formalUserAssigned = Boolean(openFormal?.user_assigned);
        const formalTakeable = isFormalStatusTakeable(openFormal?.status);
        const formalCycleActive = isOpenFormalCycleActive(openFormal?.status);
        const formalAssignmentStatus = (
          openFormal?.user_assignment_status || ""
        ).toLowerCase();
        // User already final-submitted this open Formal (still unpublished).
        const userAlreadySubmittedOpenFormal = Boolean(
          openFormalId &&
            formalCycleActive &&
            formalUserAssigned &&
            formalAssignmentStatus === "completed"
        );
        // Formal draft in progress — resume Formal, do not open Team Assess.
        const userHasOpenFormalDraft = Boolean(
          openFormalId &&
            formalTakeable &&
            formalUserAssigned &&
            formalAssignmentStatus === "in_progress"
        );
        // Explicit Formal resume (Assigned / Take Formal) — open Formal form.
        // Status Assess (freshStart) must show the pending-Formal banner first
        // unless this user already submitted (then open Formal already-submitted).
        const isExplicitFormalResume = Boolean(
          openFormalId &&
            options?.fallbackAssessmentId &&
            String(options.fallbackAssessmentId) === openFormalId
        );

        // Assignees can use Team Assess and the Formal independently. A linked
        // member not selected on the Formal is routed into it (banner + Take
        // Formal) only when no Team window is open; otherwise they use Team Assess.
        const openFormalBlocksTeam = Boolean(
          openFormalId &&
            formalTakeable &&
            !formalUserAssigned &&
            openFormal?.user_included &&
            !(pendingAssessmentId && pendingAssessmentId !== openFormalId)
        );

        const effectivePendingCount = Math.max(
          pendingCount,
          openFormalBlocksTeam ? 1 : 0
        );
        setHasFormalAssessmentPending(hasPending || openFormalBlocksTeam);
        setPendingFormalCount(effectivePendingCount);

        const syntheticOpenFormalRow: PendingFormalValidationAssessment[] =
          openFormalBlocksTeam &&
          openFormalId &&
          !formalAssessments.some(
            item => String(item.assessment_id || "") === openFormalId
          )
            ? [
                {
                  assessment_id: openFormalId,
                  assessment_name: "Formal assessment",
                  assessment_type: "AL assessment",
                  assessment_status: openFormal?.status || "ACTIVE",
                  assignment_status: formalAssignmentStatus || "pending",
                },
              ]
            : [];
        setPendingFormalAssessments([
          ...formalAssessments,
          ...syntheticOpenFormalRow,
        ]);
        // "Take Formal Assessment" on the pending-Formal banner opens this
        // Formal through the CF/RA choice (Current) or an empty form (Stale).
        const takeOpenFormalId =
          openFormalId &&
          (openFormalBlocksTeam ||
            (hasPending &&
              pendingCount <= 1 &&
              formalTakeable &&
              formalUserAssigned))
            ? openFormalId
            : null;
        setBlockingOpenFormalId(takeOpenFormalId);
        setBlockingOpenFormalStatus(
          takeOpenFormalId ? openFormal?.status || null : null
        );
        setBlockingOpenFormalUnassigned(
          takeOpenFormalId ? !formalUserAssigned : false
        );
        const hasOpenSelfFromValidation = Boolean(
          pendingSelfAssessment?.assessment_id &&
            isSelfAssessmentResumable(pendingSelfAssessment)
        );
        const pendingMatchesLatestForPresence =
          latestPayload?.self_assessment?.assessment_id ===
          pendingSelfAssessment?.assessment_id;
        const pendingStatusForPresence = (
          pendingSelfAssessment?.assessment_status ||
          latestPayload?.self_assessment?.status ||
          ""
        ).toUpperCase();
        // Leftover PRE_PUBLISH keeps draft rows in DB but must not surface as Draft.
        const latestIsPersonalDraft = Boolean(
          latestPayload?.cycle_draft?.response?.is_draft
        );
        const selfDraft = Boolean(
          latestIsPersonalDraft ||
            (hasOpenSelfFromValidation &&
              pendingMatchesLatestForPresence &&
              latestPayload?.cycle_draft?.response?.is_draft &&
              pendingStatusForPresence !== "PRE_PUBLISH")
        );
        const selfAssigned = hasOpenSelfFromValidation && !selfDraft;

        const formalDraft =
          formalAssessments.some(isFormalAssignmentDraft) ||
          userHasOpenFormalDraft;
        const formalAssigned =
          formalAssessments.some(item => {
            const status = (item.assignment_status || "").toLowerCase();
            return status === "pending" || status === "overdue";
          }) ||
          (Boolean(openFormalId) &&
            formalTakeable &&
            formalUserAssigned &&
            !formalDraft &&
            !userAlreadySubmittedOpenFormal);

        setAccountPresence({
          selfDraft,
          selfAssigned,
          formalDraft,
          formalAssigned,
        });

        // Formal Hijack: resume Formal when explicitly opening Formal, or when
        // there is no open Team for this user. Open Team stays the Status Assess
        // / Team Start Now path so both cycles remain independently completable.
        const preferAssignedFormal =
          formalCycleActive &&
          formalUserAssigned &&
          (isExplicitFormalResume ||
            ((userAlreadySubmittedOpenFormal || userHasOpenFormalDraft) &&
              !pendingAssessmentId));
        const foldIntoFormalAssessmentId =
          formalTakeable &&
          !hasPending &&
          openFormalId &&
          !formalUserAssigned &&
          !openFormalBlocksTeam &&
          !pendingAssessmentId
            ? openFormalId
            : null;
        const formalAssessmentIdToUse =
          preferAssignedFormal || foldIntoFormalAssessmentId
            ? openFormalId
            : null;

        // Completed Formal: under review — no new Self. An open disconnect does
        // not block input; the backend holds it until the disconnect is final.
        const shouldShowFinalization =
          !preferAssignedFormal &&
          !pendingAssessmentId &&
          Boolean(openFormalId) &&
          !formalTakeable;

        if (shouldShowFinalization) {
          resetModal();
          return "finalization";
        }

        // Same rule as Review Assessments status Assess: no running cycle
        // (pending_participant_count is 0). Date comes from that portfolio row.
        const cachedPortfolioRow = minePortfolioAccounts.find(
          account => account.account_id === record.id
        );
        let portfolioRow: PortfolioAccount | undefined = cachedPortfolioRow;
        if (!portfolioRow) {
          try {
            const loaded = await dispatch(
              fetchPortfolioAccounts("mine")
            ).unwrap();
            portfolioRow = loaded.accounts.find(
              account => account.account_id === record.id
            );
          } catch {
            portfolioRow = undefined;
          }
        }
        setPrivateRemainder(
          portfolioRow && !(portfolioRow.pending_participant_count ?? 0)
            ? {
                countsOn:
                  portfolioRow.your_assessment_pending_counts_on ?? null,
              }
            : null
        );

        setForceReadOnly(false);

        const applyResponsePrefill = (
          assessmentKey: string,
          prefill: AssessmentResponseMap
        ) => {
          if (!Object.keys(prefill).length) return;
          setResponses(prev => ({
            ...prev,
            [assessmentKey]: {
              ...(prev[assessmentKey] || {}),
              ...prefill,
            },
          }));
        };

        if (pendingAssessmentId && !preferAssignedFormal) {
          setIsFormalFoldIn(false);
          setTakingKind("team");
          setAssessMode(null);
          // Only a saved draft response belongs on the Draft tab; an open
          // PRE_ACTIVE cycle with no answers is Assigned.
          const isEslBufferLocked = false;
          const cycleDraft = latestPayload?.cycle_draft;
          const cycleDraftMatchesPending =
            Boolean(cycleDraft?.response?.is_draft) &&
            String(cycleDraft?.assessment_id || "") === pendingAssessmentId;
          const hasSavedDraft = Boolean(
            !isEslBufferLocked && cycleDraftMatchesPending
          );
          setIsDraftSession(hasSavedDraft);
          setSelfAssessmentId(pendingAssessmentId);
          setTemplateData({
            id: pendingAssessmentId,
            name: normalizeTeamAssessmentDisplayName(
              pendingSelfAssessment?.assessment_name ??
                latestPayload?.self_assessment?.title ??
                template.name,
              "Team Assessment"
            ),
            json_schema: template.json_schema,
            status:
              pendingSelfAssessment?.assessment_status?.toLowerCase() ??
              "in_progress",
          });

          // Clear any prior in-memory answers for this cycle when due-locked.
          if (isEslBufferLocked) {
            setResponses(prev => {
              const next = { ...prev };
              delete next[pendingAssessmentId];
              return next;
            });
          }

          if (hasSavedDraft) {
            const draftResponse = cycleDraft?.response;
            if (draftResponse) {
              applyResponsePrefill(
                pendingAssessmentId,
                mapPriorResponsesToForm(draftResponse)
              );
            }
          }

          if (
            !isEslBufferLocked &&
            options?.initialResponses &&
            Object.keys(options.initialResponses).length > 0
          ) {
            applyResponsePrefill(pendingAssessmentId, options.initialResponses);
            // Draft-tab Continue passes saved answers even if latest payload lags.
            if (
              hasSavedDraft ||
              pendingSelfAssessment?.assignment_status === "in_progress"
            ) {
              setIsDraftSession(true);
              setAccountPresence(prev => ({ ...prev, selfDraft: true }));
            }
          }

          const priorOnThisTeamCycle =
            !isEslBufferLocked &&
            latestPayload?.origin === "self" &&
            latestPayload?.self_assessment?.assessment_id ===
              pendingAssessmentId &&
            Boolean(latestPayload?.has_response) &&
            !latestPayload?.response?.is_draft;
          if (priorOnThisTeamCycle) {
            const priorResponse = latestPayload?.response;
            if (priorResponse && !options?.initialResponses && !hasSavedDraft) {
              applyResponsePrefill(
                pendingAssessmentId,
                mapPriorResponsesToForm(priorResponse)
              );
            }
            if (!options?.reassessBanner) {
              setReassessBanner({
                submittedAt:
                  priorResponse?.updated_at ||
                  priorResponse?.submitted_at ||
                  null,
                dueDate:
                  latestPayload?.self_assessment?.due_date ||
                  pendingSelfAssessment?.due_date ||
                  null,
              });
            }
          } else if (
            !isEslBufferLocked &&
            options?.initialResponses &&
            Object.keys(options.initialResponses).length > 0 &&
            !options?.reassessBanner &&
            !hasSavedDraft
          ) {
            setReassessBanner({
              submittedAt:
                latestPayload?.response?.updated_at ||
                latestPayload?.response?.submitted_at ||
                null,
              dueDate:
                latestPayload?.self_assessment?.due_date ||
                pendingSelfAssessment?.due_date ||
                null,
            });
          }

          // Status / Portfolio Assess omit teamPeerBanner — resolve from co-assignees.
          if (
            options?.teamPeerBanner === undefined &&
            !isEslBufferLocked &&
            !priorOnThisTeamCycle
          ) {
            const executiveId = getLoggedInExecutiveId(
              getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER) as Record<
                string,
                unknown
              > | null
            );
            const autoBanner = await fetchPendingTeamPeerBannerForAccount(
              dispatch,
              record.id,
              pendingAssessmentId,
              executiveId
            );
            if (autoBanner) {
              setTeamPeerBanner(autoBanner);
            }
          }
        } else if (formalAssessmentIdToUse) {
          // Submitting onto the Formal itself — do not block as "complete Formal first".
          setHasFormalAssessmentPending(false);
          setPendingFormalCount(0);
          setBlockingOpenFormalId(null);
          setBlockingOpenFormalStatus(null);
          setBlockingOpenFormalUnassigned(false);
          setIsFormalFoldIn(Boolean(foldIntoFormalAssessmentId));
          setTakingKind("formal");
          setTeamPeerBanner(null);
          setAssessMode(null);
          setSelfAssessmentId(null);

          const executiveId = getLoggedInExecutiveId(
            getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER) as Record<
              string,
              unknown
            > | null
          );
          const formalResume =
            executiveId &&
            (userAlreadySubmittedOpenFormal ||
              userHasOpenFormalDraft ||
              isExplicitFormalResume)
              ? await loadFormalResumeForAccount(
                  dispatch,
                  record.id,
                  formalAssessmentIdToUse,
                  executiveId
                )
              : null;

          const formalName =
            formalResume?.assessmentName || template.name || "Assessment";
          setTemplateData({
            id: formalAssessmentIdToUse,
            name: formalName,
            json_schema: formalResume?.questions || template.json_schema,
            status: (
              formalResume?.status ||
              openFormal?.status ||
              "in_progress"
            ).toLowerCase(),
          });

          const resumeAnswers = formalResume?.response ?? null;
          const hasResumeAnswers =
            Boolean(resumeAnswers) &&
            Object.keys(resumeAnswers as object).length > 0;
          const resumeIsDraft = Boolean(
            formalResume?.isDraft && hasResumeAnswers
          );

          if (options?.initialResponses) {
            applyResponsePrefill(
              formalAssessmentIdToUse,
              options.initialResponses
            );
            if (Object.keys(options.initialResponses).length > 0) {
              setIsDraftSession(true);
              setAccountPresence(prev => ({ ...prev, formalDraft: true }));
            }
          } else if (resumeIsDraft && resumeAnswers) {
            setIsDraftSession(true);
            setAccountPresence(prev => ({ ...prev, formalDraft: true }));
            applyResponsePrefill(formalAssessmentIdToUse, resumeAnswers);
          } else if (
            formalResume?.alreadySubmitted &&
            resumeAnswers &&
            !options?.reassessBanner
          ) {
            setIsDraftSession(false);
            applyResponsePrefill(formalAssessmentIdToUse, resumeAnswers);
            setReassessBanner({
              submittedAt: formalResume?.submittedAt ?? null,
              dueDate: formalResume?.dueDate || openFormal?.due_date || null,
            });
          } else if (
            userAlreadySubmittedOpenFormal &&
            !options?.reassessBanner
          ) {
            // Completed Formal found without answer payload — still show
            // already-submitted banner using validation due date.
            setIsDraftSession(false);
            setReassessBanner({
              submittedAt: formalResume?.submittedAt ?? null,
              dueDate: formalResume?.dueDate || openFormal?.due_date || null,
            });
          }
        } else {
          setIsFormalFoldIn(false);
          setTakingKind("team");

          setSelfAssessmentId(null);
          setTemplateData({
            id: templateId,
            name: normalizeTeamAssessmentDisplayName(
              template.name,
              "Team Assessment"
            ),
            json_schema: template.json_schema,
            status: "in_progress",
          });

          if (options?.initialResponses) {
            applyResponsePrefill(templateId, options.initialResponses);
            if (Object.keys(options.initialResponses).length > 0) {
              setIsDraftSession(true);
              setAccountPresence(prev => ({ ...prev, selfDraft: true }));
            }
          }
        }

        const priorSubmitted =
          Boolean(latestPayload?.has_response) &&
          !latestPayload?.response?.is_draft;
        const priorSubmittedAt =
          latestPayload?.response?.submitted_at ??
          latestPayload?.response?.updated_at ??
          null;
        const myCadenceCurrent =
          priorSubmitted && Boolean(latestPayload?.input_current);

        // Draft on the open Team cycle (cycle_draft) vs prior for resume rule.
        const openTeamDraft = latestPayload?.cycle_draft;
        const openTeamDraftOnPending =
          Boolean(pendingAssessmentId) &&
          !preferAssignedFormal &&
          Boolean(openTeamDraft?.response?.is_draft) &&
          String(openTeamDraft?.assessment_id || "") === pendingAssessmentId;
        const openTeamDraftUpdatedAt =
          openTeamDraft?.response?.updated_at ||
          openTeamDraft?.response?.submitted_at ||
          null;
        const resumeOpenTeamDraft =
          openTeamDraftOnPending &&
          isDraftNewerThanPrior(openTeamDraftUpdatedAt, priorSubmittedAt);

        // Non-assigned Formal: open Formal form directly (no Confirm modal).
        if (foldIntoFormalAssessmentId) {
          setGateBOptions({
            formalAssessmentId: foldIntoFormalAssessmentId,
            selfTemplateId: templateId,
          });
          setIsFormalFoldIn(true);
          setTakingKind("formal");
          setTeamPeerBanner(null);
          setHasFormalAssessmentPending(false);
          setPendingFormalCount(0);
          setBlockingOpenFormalId(null);
          setBlockingOpenFormalStatus(null);
          setBlockingOpenFormalUnassigned(false);
          setTemplateData(prev =>
            prev ? { ...prev, id: foldIntoFormalAssessmentId } : prev
          );
          setQuestionIndex(0);
          if (myCadenceCurrent) {
            setModalStep("choice");
          } else {
            setAssessMode(null);
            setPriorResponseId(null);
            setResponses(prev => {
              const next = { ...prev };
              delete next[foldIntoFormalAssessmentId];
              return next;
            });
            setModalStep("form");
          }
          return "opened";
        }

        // Assigned Formal blocks Team Assess — show Formal-pending form banner.
        if (openFormalBlocksTeam) {
          setModalStep("form");
          return "opened";
        }

        // Pending Formal: banner first (no Team CF/RA); Take Formal then
        // opens the Formal CF/RA choice when Current.
        if (hasPending && !formalAssessmentIdToUse) {
          setAssessMode(null);
          setPriorResponseId(null);
          setModalStep("form");
          return "opened";
        }

        // Already submitted / Formal draft → Formal form (not Team CF/RA).
        if (
          (userAlreadySubmittedOpenFormal || userHasOpenFormalDraft) &&
          formalAssessmentIdToUse
        ) {
          setModalStep("form");
          return "opened";
        }

        // Newer draft on open Team cycle → resume draft (skip CF/RA).
        if (resumeOpenTeamDraft) {
          setModalStep("form");
          return "opened";
        }

        // Older draft than prior → ignore draft answers; CF/RA if Current.
        if (
          openTeamDraftOnPending &&
          !resumeOpenTeamDraft &&
          pendingAssessmentId
        ) {
          setIsDraftSession(false);
          setResponses(prev => {
            const next = { ...prev };
            delete next[pendingAssessmentId];
            return next;
          });
        }

        if (myCadenceCurrent) {
          setModalStep("choice");
        } else {
          // Stale / Unassessed → empty form (no prior prefill).
          if (pendingAssessmentId && !preferAssignedFormal) {
            setResponses(prev => {
              const next = { ...prev };
              delete next[pendingAssessmentId];
              return next;
            });
          } else if (formalAssessmentIdToUse && !options?.initialResponses) {
            setResponses(prev => {
              const next = { ...prev };
              delete next[formalAssessmentIdToUse];
              return next;
            });
          } else if (!pendingAssessmentId && !formalAssessmentIdToUse) {
            setResponses(prev => {
              const next = { ...prev };
              delete next[templateId];
              return next;
            });
          }
          setAssessMode(null);
          setPriorResponseId(null);
          setModalStep("form");
        }
        return "opened";
      } catch {
        toast.error("Unable to validate assessment eligibility.");
        resetModal();
        return "error";
      } finally {
        setIsValidationLoading(false);
      }
    },
    [template, resetModal, dispatch, minePortfolioAccounts]
  );

  const continueFromChoice = useCallback(
    (choice: PortfolioAssessChoice) => {
      if (!template || !latestSelfData?.has_response) return;
      const formId =
        templateData?.id || selfAssessmentId || getTemplateId(template);
      if (!formId) return;
      setAssessMode(choice);
      // Keep takingKind / Formal fold-in as already configured for this open.
      applyPriorResponsePrefill(formId, latestSelfData);
      setQuestionIndex(0);
      setModalStep("form");
    },
    [
      template,
      templateData?.id,
      selfAssessmentId,
      latestSelfData,
      applyPriorResponsePrefill,
    ]
  );

  const backToChoice = useCallback(() => {
    setModalStep("choice");
  }, []);

  /** After Formal fold-in confirmation: CF/RA if Current, else empty form. */
  const continueFormalFromGateB = useCallback(() => {
    if (!gateBOptions) return;
    setSelfAssessmentId(null);
    setIsFormalFoldIn(true);
    setTakingKind("formal");
    setTeamPeerBanner(null);
    setHasFormalAssessmentPending(false);
    setPendingFormalCount(0);
    setBlockingOpenFormalId(null);
    setBlockingOpenFormalStatus(null);
    setBlockingOpenFormalUnassigned(false);
    setTemplateData(prev =>
      prev ? { ...prev, id: gateBOptions.formalAssessmentId } : prev
    );
    setQuestionIndex(0);

    const priorSubmitted =
      Boolean(latestSelfData?.has_response) &&
      !latestSelfData?.response?.is_draft;
    if (priorSubmitted && latestSelfData?.input_current) {
      setModalStep("choice");
    } else {
      // Stale / Unassessed → empty Formal form
      setAssessMode(null);
      setPriorResponseId(null);
      setResponses(prev => {
        const next = { ...prev };
        delete next[gateBOptions.formalAssessmentId];
        return next;
      });
      setModalStep("form");
    }
  }, [gateBOptions, latestSelfData]);

  /** Team start while Formal is running is no longer allowed. */
  const startSelfFromGateB = useCallback(() => {
    resetModal();
  }, [resetModal]);

  const takeBlockingFormalAssessment =
    useCallback(async (): Promise<boolean> => {
      if (!blockingOpenFormalId || !template || !accountId) return false;
      if (isTakeFormalLoading) return true;

      const formalId = blockingOpenFormalId;
      const formalStatus = blockingOpenFormalStatus;
      const wasUnassigned = blockingOpenFormalUnassigned;
      const loggedInUser = getFromLocalStorage(
        STORAGE_KEYS.LOGGED_IN_USER
      ) as Record<string, unknown> | null;
      const executiveId = getLoggedInExecutiveId(loggedInUser);

      setIsTakeFormalLoading(true);
      try {
        const resume = executiveId
          ? await loadFormalResumeForAccount(
              dispatch,
              accountId,
              formalId,
              executiveId
            )
          : null;

        const resolvedId = resume?.assessmentId || formalId;
        const prefill = resume?.response ?? null;
        const hasPrefill = Boolean(prefill && Object.keys(prefill).length > 0);
        const hasFormalDraft = Boolean(resume?.isDraft && hasPrefill);

        setHasFormalAssessmentPending(false);
        setPendingFormalCount(0);
        setBlockingOpenFormalId(null);
        setBlockingOpenFormalStatus(null);
        setBlockingOpenFormalUnassigned(false);
        setSelfAssessmentId(null);
        setIsFormalFoldIn(wasUnassigned);
        setTakingKind("formal");
        setTeamPeerBanner(null);
        setAssessMode(null);

        setTemplateData({
          id: resolvedId,
          name: resume?.assessmentName || template.name || "Assessment",
          json_schema: resume?.questions || template.json_schema,
          status: (
            resume?.status ||
            formalStatus ||
            "in_progress"
          ).toLowerCase(),
        });

        if (hasFormalDraft && prefill) {
          setIsDraftSession(true);
          setAccountPresence(prev => ({ ...prev, formalDraft: true }));
          setResponses(prev => ({
            ...prev,
            [resolvedId]: { ...(prev[resolvedId] || {}), ...prefill },
          }));
        } else {
          setIsDraftSession(false);
          setResponses(prev => {
            const next = { ...prev };
            delete next[resolvedId];
            return next;
          });
        }

        setQuestionIndex(0);

        // Non-assigned → open Formal directly (no Confirm Formal Response modal).
        if (wasUnassigned) {
          setIsFormalFoldIn(true);
          setTakingKind("formal");
          if (hasFormalDraft) {
            setModalStep("form");
            return true;
          }
          if (resume?.alreadySubmitted && prefill) {
            setResponses(prev => ({
              ...prev,
              [resolvedId]: { ...(prev[resolvedId] || {}), ...prefill },
            }));
            setReassessBanner({
              submittedAt: resume?.submittedAt ?? null,
              dueDate: resume?.dueDate ?? null,
            });
            setModalStep("form");
            return true;
          }
          const priorSubmitted =
            Boolean(latestSelfData?.has_response) &&
            !latestSelfData?.response?.is_draft;
          if (priorSubmitted && latestSelfData?.input_current) {
            setModalStep("choice");
          } else {
            setAssessMode(null);
            setPriorResponseId(null);
            setModalStep("form");
          }
          return true;
        }

        // Assigned Formal: resume draft if present; else already-submitted
        // Formal when answers exist; else CF/RA when Current.
        if (hasFormalDraft) {
          setModalStep("form");
          return true;
        }

        if (resume?.alreadySubmitted && prefill) {
          setResponses(prev => ({
            ...prev,
            [resolvedId]: { ...(prev[resolvedId] || {}), ...prefill },
          }));
          setReassessBanner({
            submittedAt: resume?.submittedAt ?? null,
            dueDate: resume?.dueDate ?? null,
          });
          setModalStep("form");
          return true;
        }

        const priorSubmitted =
          Boolean(latestSelfData?.has_response) &&
          !latestSelfData?.response?.is_draft;
        if (priorSubmitted && latestSelfData?.input_current) {
          setModalStep("choice");
        } else {
          setModalStep("form");
        }
        return true;
      } catch {
        toast.error("Unable to open the formal assessment.");
        return false;
      } finally {
        setIsTakeFormalLoading(false);
      }
    }, [
      dispatch,
      blockingOpenFormalId,
      blockingOpenFormalStatus,
      blockingOpenFormalUnassigned,
      template,
      accountId,
      isTakeFormalLoading,
      resetModal,
      latestSelfData,
      dispatch,
    ]);

  const handleResponseChange = useCallback(
    (questionId: string, value: string | string[]) => {
      if (!responseKey) return;
      setResponses(prev => ({
        ...prev,
        [responseKey]: { ...(prev[responseKey] || {}), [questionId]: value },
      }));
    },
    [responseKey]
  );

  const toggleVoice = useCallback(
    (next: boolean) => {
      if (!speechSupported && next) {
        setVoiceEnabled(false);
        return;
      }
      setVoiceEnabled(next);
      if (next) startListening();
      else stopListening();
    },
    [speechSupported, startListening, stopListening]
  );

  const voiceMode = useMemo(
    () => ({
      enabled: voiceEnabled && speechSupported,
      supported: speechSupported,
      isListening,
      onToggle: toggleVoice,
      showInstructions: voiceEnabled && speechSupported,
    }),
    [voiceEnabled, speechSupported, isListening, toggleVoice]
  );

  useEffect(() => {
    dispatch(fetchSelfAssessmentConfig());
  }, [dispatch]);

  return {
    showChoiceModal,
    showGateBModal,
    continueFormalFromGateB,
    startSelfFromGateB,
    showFormModal,
    templateData,
    responses,
    accountName,
    questionIndex,
    setQuestionIndex,
    isSubmitting,
    isValidationLoading,
    hasFormalAssessmentPending,
    pendingFormalCount,
    pendingFormalAssessments,
    formalAssessmentPendingMessage: hasFormalAssessmentPending
      ? blockingOpenFormalUnassigned && pendingFormalCount <= 1
        ? buildFormalIncludedMessage(accountName)
        : buildFormalAssessmentPendingMessage(pendingFormalCount)
      : null,
    accountId,
    submitAction,
    voiceMode,
    resetModal,
    openForAccount,
    continueFromChoice,
    backToChoice,
    handleResponseChange,
    latestSelfData,
    assessMode,
    carryForwardNote,
    setCarryForwardNote,
    portfolioFormFlow,
    portfolioStatusBadge,
    orgCurrencyDays,
    submitSuccessInfo,
    closeSuccessModal,
    isFormalFoldIn,
    isDraftSession,
    forceReadOnly,
    teamPeerBanner,
    reassessBanner,
    privateRemainder,
    takingKind,
    myAssessmentsNavTarget: resolveMyAssessmentsNavTarget(accountPresence, {
      isDraftSession,
      takingKind,
    }),
    saveDraft: () => saveAssessment(true, "draft"),
    submit: () => saveAssessment(false, "submit", true),
    takeBlockingFormalAssessment,
    isTakeFormalLoading,
    goToMyAssessments: async (): Promise<boolean> => {
      const target = resolveMyAssessmentsNavTarget(accountPresence, {
        isDraftSession,
        takingKind,
      });
      if (!target) return false;
      resetModal();
      navigate(
        withOrg(`/pending-assessments?tab=${target.tab}&kind=${target.kind}`)
      );
      return true;
    },
  };
};

// ─── Main Component ────────────────────────────────────────────────────────

type MainTab =
  | "today"
  | "account_priorities"
  | "portfolio_goals"
  | "relationship_priorities"
  | "actions";

/** List | Maps under Account Priorities (Maps replaces Bird's Eye). */
type AccountPrioritiesView = "list" | "maps";

/** ?tab= slugs — read by the Guide Drawer's page-awareness matcher
 * (Utils/Guidance/guidanceLinkResolver.ts) to detect which tab is active. */
const MAIN_TAB_SLUGS: Record<MainTab, string> = {
  today: "today",
  account_priorities: "account-priorities",
  portfolio_goals: "portfolio-goals",
  relationship_priorities: "relationship-priorities",
  actions: "actions",
};
const SLUG_TO_MAIN_TAB: Record<string, MainTab> = {
  today: "today",
  "account-priorities": "account_priorities",
  "portfolio-goals": "portfolio_goals",
  "relationship-priorities": "relationship_priorities",
  actions: "actions",
  // Back-compat: former top-level Portfolio Map / Maps → Account Priorities → Maps.
  "portfolio-map": "account_priorities",
  // Back-compat for the merged Priorities tab.
  priorities: "account_priorities",
};

const MyPortfolioAccounts = () => {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const withOrg = useCallback(
    (path: string) => {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
    },
    [orgSlug]
  );

  const { templates: templatesPayload } = useAppSelector(
    state => state.assessment
  );

  const overviewState = useAppSelector(state => state.myPortfolioOverview);
  const overviewData = overviewState.data;
  const overviewLoading = overviewState.loading;
  const signalFeedCards = useAppSelector(state => state.signalFeed.cards);
  const {
    signalsVisible,
    actionsSurface,
    configuredActionsSurface,
    status: presentationStatus,
  } = useSignalPresentation();
  const {
    mapsEnabled,
    config: portfolioMapsConfig,
    resolved: mapsConfigResolved,
  } = usePortfolioMapLenses();
  // Wait for admin config before showing Maps chrome (avoids layout flash).
  const mapsTabVisible =
    mapsConfigResolved &&
    mapsEnabled &&
    Object.values(portfolioMapsConfig.lenses).some(Boolean);
  /** Only a configured Open Actions = Disabled greys out / blocks the tab.
   * Inactive + Enabled still opens Actions (off-notice + history). */
  const actionsTabDisabled =
    presentationStatus === "ready" && configuredActionsSurface === "disabled";
  /** Open Actions Hidden — remove the Actions tab and all Open Actions CTAs.
   * Only once the policy has loaded: an unknown policy (still loading, or
   * the first feed failed) is not Hidden, so a ?tab=actions deep link or
   * refresh stays on Actions and that tab shows loading / error (S-02). */
  const actionsTabHidden =
    presentationStatus === "ready" && actionsSurface === "hidden";
  /** Signal CTAs can't deep-link into Actions until the policy says enabled. */
  const actionsEntryBlocked = actionsSurface !== "enabled";

  const selfAssessmentTemplate = useMemo(
    () => resolveSelfAssessmentTemplate(templatesPayload),
    [templatesPayload]
  );

  const selfAssessment = usePortfolioSelfAssessment(
    selfAssessmentTemplate,
    withOrg
  );

  const [activeTab, setActiveTab] = useState<MainTab>(() => {
    const fromQuery = searchParams.get("tab");
    // Back-compat: ?tab=priorities&view=relationship → Relationship Priorities.
    if (
      fromQuery === "priorities" &&
      searchParams.get("view") === "relationship"
    ) {
      return "relationship_priorities";
    }
    const mapped = fromQuery ? SLUG_TO_MAIN_TAB[fromQuery] : undefined;
    // Today tab temporarily hidden — deep links fall back to Account Priorities.
    if (!mapped || mapped === "today") return "account_priorities";
    return mapped;
  });
  const [accountPrioritiesView, setAccountPrioritiesView] =
    useState<AccountPrioritiesView>(() => {
      const fromQuery = searchParams.get("tab");
      if (
        fromQuery === "portfolio-map" ||
        searchParams.get("view") === "maps"
      ) {
        return "maps";
      }
      return "list";
    });
  const changeTab = useCallback(
    (tab: MainTab) => {
      // Today tab temporarily hidden.
      if (tab === "today") {
        setActiveTab("account_priorities");
        setAccountPrioritiesView("list");
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.set("tab", MAIN_TAB_SLUGS.account_priorities);
          next.delete("view");
          return next;
        });
        return;
      }
      // Hidden removes the tab; Disabled keeps it visible but not selectable.
      if (tab === "actions" && (actionsTabHidden || actionsTabDisabled)) return;
      setActiveTab(tab);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("tab", MAIN_TAB_SLUGS[tab]);
        if (tab !== "account_priorities") {
          next.delete("view");
        } else if (accountPrioritiesView === "maps") {
          next.set("view", "maps");
        } else {
          next.delete("view");
        }
        return next;
      });
    },
    [
      actionsTabHidden,
      actionsTabDisabled,
      accountPrioritiesView,
      setSearchParams,
    ]
  );
  const changeAccountPrioritiesView = useCallback(
    (view: AccountPrioritiesView) => {
      const nextView = view === "maps" && !mapsTabVisible ? "list" : view;
      setAccountPrioritiesView(nextView);
      setActiveTab("account_priorities");
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("tab", MAIN_TAB_SLUGS.account_priorities);
        if (nextView === "maps") next.set("view", "maps");
        else next.delete("view");
        return next;
      });
    },
    [mapsTabVisible, setSearchParams]
  );

  // Izba Admin can hide Maps — drop off the Maps subtab and clear deep links.
  // Wait until config is loaded so we don't yank Maps→List before knowing.
  useEffect(() => {
    if (!mapsConfigResolved) return;
    if (mapsTabVisible) return;
    if (accountPrioritiesView !== "maps") return;
    setAccountPrioritiesView("list");
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete("view");
        return next;
      },
      { replace: true }
    );
  }, [
    mapsConfigResolved,
    mapsTabVisible,
    accountPrioritiesView,
    setSearchParams,
  ]);
  // Keep activeTab in sync when the URL changes externally (deep link, back/forward).
  useEffect(() => {
    const fromQuery = searchParams.get("tab");
    // Back-compat: ?tab=priorities&view=relationship → Relationship Priorities.
    if (
      fromQuery === "priorities" &&
      searchParams.get("view") === "relationship"
    ) {
      if (activeTab !== "relationship_priorities") {
        setActiveTab("relationship_priorities");
      }
      return;
    }
    const mapped = fromQuery ? SLUG_TO_MAIN_TAB[fromQuery] : undefined;
    // Today tab temporarily hidden — rewrite ?tab=today to account-priorities.
    if (mapped === "today") {
      setActiveTab("account_priorities");
      setAccountPrioritiesView("list");
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set("tab", MAIN_TAB_SLUGS.account_priorities);
          next.delete("view");
          return next;
        },
        { replace: true }
      );
      return;
    }
    if (mapped && mapped !== activeTab) {
      setActiveTab(mapped);
    }
    if (mapped === "account_priorities") {
      // Don't resolve List vs Maps until admin config is ready — avoids
      // disabled chrome flashing before the enabled layout.
      if (!mapsConfigResolved) return;
      const wantsMaps =
        fromQuery === "portfolio-map" || searchParams.get("view") === "maps";
      const nextView: AccountPrioritiesView =
        wantsMaps && mapsTabVisible ? "maps" : "list";
      setAccountPrioritiesView(prev => (prev === nextView ? prev : nextView));
      if (wantsMaps && !mapsTabVisible) {
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev);
            next.delete("view");
            return next;
          },
          { replace: true }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, mapsTabVisible, mapsConfigResolved]);
  const [tileFilter, setTileFilter] = useState<TileFilter>(null);
  const notifyPastDueLocked = () => {
    toast.info(
      "The due date has passed. You can view a submitted response, or there is no response submitted."
    );
  };
  /** Cross-tab "manual comparison" working set, seeded from Portfolio Map's
   * multi-select (shift-click/drag). Not URL-persisted, consistent with
   * every other sub-view's state in this file (lens key aside, which uses
   * sessionStorage for its own reason). null = no active working set. */
  const [workingSetAccountIds, setWorkingSetAccountIds] = useState<
    string[] | null
  >(null);
  const clearWorkingSet = useCallback(() => setWorkingSetAccountIds(null), []);
  /** One-shot Actions deep-link (e.g. Portfolio Map's "See all portfolio
   * actions →"/"View in Actions →") — URL-serialized like `?tab=`, cleared
   * once ActionsTab consumes it. */
  const [highlightAccountId, setHighlightAccountId] = useState<string | null>(
    () => searchParams.get("highlightAccountId")
  );
  const [highlightSignalKey, setHighlightSignalKey] = useState<string | null>(
    () => searchParams.get("highlightSignalKey")
  );
  const clearHighlight = useCallback(() => {
    setHighlightAccountId(null);
    setHighlightSignalKey(null);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("highlightAccountId");
      next.delete("highlightSignalKey");
      return next;
    });
  }, [setSearchParams]);
  const goToActionsWithHighlight = useCallback(
    (accountId?: string, signalKey?: string) => {
      if (actionsEntryBlocked) return;
      setActiveTab("actions");
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("tab", MAIN_TAB_SLUGS.actions);
        if (accountId) next.set("highlightAccountId", accountId);
        if (signalKey) next.set("highlightSignalKey", signalKey);
        return next;
      });
      if (accountId) setHighlightAccountId(accountId);
      setHighlightSignalKey(signalKey ?? null);
    },
    [actionsEntryBlocked, setSearchParams]
  );

  // Leave Actions if Open Actions is Hidden or Disabled while the user is
  // already on that tab (or lands via a stale ?tab=actions). Disabled keeps
  // the tab in the nav but it is not selectable.
  useEffect(() => {
    if (activeTab !== "actions") return;
    if (!actionsTabHidden && !actionsTabDisabled) return;
    // Wait for policy so a cold ?tab=actions load isn't bounced before we know.
    if (presentationStatus !== "ready") return;
    changeTab("account_priorities");
  }, [
    actionsTabHidden,
    actionsTabDisabled,
    presentationStatus,
    activeTab,
    changeTab,
  ]);
  const headerFiltersRef = useRef<HTMLDivElement>(null);
  const [prioritiesFilterBarEl, setPrioritiesFilterBarEl] =
    useState<HTMLDivElement | null>(null);
  const [mapsLensBarEl, setMapsLensBarEl] = useState<HTMLDivElement | null>(
    null
  );
  const [mapsFocus, setMapsFocus] = useState<{
    accountIds: string[];
    health?: "business_health" | "account_health";
  } | null>(null);
  const clearMapsFocus = useCallback(() => setMapsFocus(null), []);
  /** One-shot Relationship Priorities focus (from Account Priorities Pairs). */
  const [relPrioritiesFocus, setRelPrioritiesFocus] = useState<{
    accountId: string;
    accountName: string;
    suppressPairsFocusModal?: boolean;
  } | null>(null);
  const clearRelPrioritiesFocus = useCallback(
    () => setRelPrioritiesFocus(null),
    []
  );

  const openAccountOnMap = useCallback(
    (accountId: string, health: "business_health" | "account_health") => {
      if (!mapsTabVisible) return;
      setMapsFocus({ accountIds: [accountId], health });
      changeAccountPrioritiesView("maps");
    },
    [changeAccountPrioritiesView, mapsTabVisible]
  );

  const goToRelationshipPrioritiesForAccount = useCallback(
    (
      account: { customer_id: string; customer_name?: string | null },
      options?: { suppressPairsFocusModal?: boolean }
    ) => {
      const name = (account.customer_name || "").trim();
      const accountId = String(account.customer_id || "").trim();
      if (name || accountId) {
        setRelPrioritiesFocus({
          accountId,
          accountName: name || accountId,
          suppressPairsFocusModal: options?.suppressPairsFocusModal,
        });
      }
      changeTab("relationship_priorities");
    },
    [changeTab]
  );

  const openAccountsOnMap = useCallback(
    (accountIds: string[]) => {
      if (!mapsTabVisible || accountIds.length === 0) return;
      setMapsFocus({ accountIds });
      changeAccountPrioritiesView("maps");
    },
    [changeAccountPrioritiesView, mapsTabVisible]
  );

  const { user } = useAppSelector(state => state.authentication);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const canOpenPortfolioSettings =
    isRole(loggedInUser?.role, ROLES.VENDOR_ADMIN) ||
    isRole(loggedInUser?.role, ROLES.EXECUTIVE_SPONSOR_LEAD);
  const [portfolioSettingsOpen, setPortfolioSettingsOpen] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchMyPortfolioOverview());
  }, [dispatch]);

  // mock: the Today header badge (`#today-badge`) and tab count (`#tc-today`)
  // are populated on page load (DOMContentLoaded), not only when the Today
  // tab/panel is opened — dispatch here, once, so both TodayOverlayPanel's
  // badge and the tab count stay live regardless of which tab is active.
  useEffect(() => {
    dispatch(fetchSignalFeed());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAssessmentTemplates({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchOrganizationLevels());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchRlSettingConfig());
  }, [dispatch]);

  const rlSettingValue = useAppSelector(
    state => state.vendorAdmin.rlSettingValue
  );

  const accounts = overviewData?.accounts ?? [];
  const summary = overviewData?.summary ?? {
    total_accounts: 0,
    primary_count: 0,
    secondary_count: 0,
    executive_count: 0,
    total_revenue: 0,
    needs_attention_count: 0,
    renewals_90d_count: 0,
    unmapped_accounts: 0,
    unassessed_pairs: 0,
    account_capacity_used: 0,
    account_capacity_limit: null,
    relationship_pairs_total: 0,
    relationship_pairs_limit: null,
  };
  const pairs = overviewData?.relationship_pairs ?? [];

  /**
   * Fan-in: max vendor contacts mapped to any one customer contact across
   * portfolio accounts (team-wide — not limited to the logged-in user's pairs).
   */
  const maxFanIn = useMemo(() => {
    const sponsorsByContact = new Map<string, Set<string>>();
    for (const pair of pairs) {
      if (!pair.contact_id) continue;
      const sponsorKey = pair.sponsor_id || (pair.is_mine ? "me" : null);
      if (!sponsorKey) continue;
      const sponsors = sponsorsByContact.get(pair.contact_id) ?? new Set();
      sponsors.add(String(sponsorKey));
      sponsorsByContact.set(pair.contact_id, sponsors);
    }
    if (sponsorsByContact.size === 0) return 0;
    let max = 0;
    sponsorsByContact.forEach(sponsors => {
      max = Math.max(max, sponsors.size);
    });
    return max;
  }, [pairs]);

  const fanInCap = normalizeFanInCap(rlSettingValue?.fan_in_cap);
  const viewerGrade = useMemo(() => {
    const authUser = user?.user as
      | { grade?: number; ladderLevel?: number; ladder_level?: number }
      | undefined;
    const gradeRaw =
      authUser?.grade ??
      authUser?.ladderLevel ??
      authUser?.ladder_level ??
      loggedInUser?.grade ??
      loggedInUser?.ladderLevel ??
      loggedInUser?.ladder_level;
    const grade = Number(gradeRaw);
    return Number.isFinite(grade) ? grade : null;
  }, [user, loggedInUser]);

  const fanOutCap = useMemo(() => {
    const caps = normalizeFanOutCaps(rlSettingValue?.fan_out_caps);
    return resolveFanOutCap(caps, viewerGrade);
  }, [rlSettingValue?.fan_out_caps, viewerGrade]);

  /** Portfolio fan-out soft limit: the org-wide Fan-out (over-all) cap. */
  const fanOutPortfolioLimit = useMemo(
    () => normalizeOverallFanOutCap(rlSettingValue?.overall_fan_out_cap),
    [rlSettingValue?.overall_fan_out_cap]
  );

  const levelMix = overviewData?.level_mix ?? { al: [], rl: [] };
  const relationshipAttentionCount = pairs.filter(
    p =>
      p.is_mine &&
      ["unassessed", "no_goal_set", "low_score"].includes(p.attention_bucket)
  ).length;

  // mock: `#tc-today`/`#tc-actions` tab-count pills count only critical+
  // warning cards from the (top-5-capped) feed, not every active firing —
  // TodayTab.tsx/TodayOverlayPanel.tsx apply the same top-5 cap, so this
  // count is derived the same way for consistency with what those surfaces
  // actually display.
  // Today tab temporarily hidden — restore with the Today tab entry below.
  // const todayCritWarnCount = useMemo(
  //   () =>
  //     signalFeedCards
  //       .slice(0, 5)
  //       .filter(c => c.tier === "critical" || c.tier === "warning").length,
  //   [signalFeedCards]
  // );
  const actionsTabCount = signalsVisible ? signalFeedCards.length : 0;
  // While the policy is loading, show the Actions tab only to someone
  // already on it (deep link / refresh), so it doesn't flash up for orgs
  // where it turns out Hidden. A failed load keeps it, to show the error.
  const showActionsTab =
    presentationStatus === "loading"
      ? activeTab === "actions"
      : !actionsTabHidden;

  const mainTabs = useMemo(() => {
    const tabIconClass = "h-4 w-4";
    const tabs = [
      // Today tab temporarily hidden.
      // {
      //   id: "today",
      //   label: (
      //     <span className="inline-flex items-center gap-1.5">
      //       Today
      //       {todayCritWarnCount > 0 && (
      //         <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-brand-faint px-1.5 py-px text-[10.5px] font-bold leading-none text-brand">
      //           {todayCritWarnCount}
      //         </span>
      //       )}
      //     </span>
      //   ),
      //   icon: <Flag className={tabIconClass} aria-hidden />,
      // },
      {
        id: "account_priorities",
        label: "Account Priorities",
        icon: <Building2 className={tabIconClass} aria-hidden />,
      },
      {
        id: "portfolio_goals",
        label: "Portfolio Grid",
        icon: <Target className={tabIconClass} aria-hidden />,
      },
      {
        id: "relationship_priorities",
        label: "Relationship Priorities",
        icon: <Users className={tabIconClass} aria-hidden />,
      },
      ...(showActionsTab
        ? [
            {
              id: "actions",
              // Configured Disabled: visible but not clickable.
              disabled: actionsTabDisabled,
              label: actionsTabDisabled ? (
                <span title={SIGNALS_OFF_MESSAGE}>Actions</span>
              ) : (
                "Actions"
              ),
              icon: <Bell className={tabIconClass} aria-hidden />,
              // No count when Inactive / no signal output — don't show a "0".
              ...(signalsVisible && !actionsTabDisabled
                ? { count: actionsTabCount }
                : {}),
            },
          ]
        : []),
    ];
    return tabs;
  }, [
    summary.total_accounts,
    relationshipAttentionCount,
    pairs.length,
    actionsTabCount,
    actionsTabDisabled,
    signalsVisible,
    showActionsTab,
    // todayCritWarnCount,
  ]);

  const accountPrioritiesSubTabs = useMemo(
    () => [
      {
        id: "list",
        label: "List",
        icon: <LayoutList className="h-3.5 w-3.5" aria-hidden />,
      },
      ...(mapsTabVisible
        ? [
            {
              id: "maps",
              label: "Maps",
              icon: <MapIcon className="h-3.5 w-3.5" aria-hidden />,
            },
          ]
        : []),
    ],
    [mapsTabVisible]
  );

  return (
    <div className="mt-3 min-h-[calc(100vh-120px)] overflow-hidden bg-white">
      <PageHeader
        title="My Accounts"
        hideBreadcrumb
        icon={Briefcase}
        className="!shadow-sm"
        actions={[
          // Today header button + overlay temporarily hidden.
          // <TodayOverlayPanel
          //   key="today-overlay"
          //   withOrg={withOrg}
          //   onOpenAssess={account =>
          //     void selfAssessment.openForAccount(account, {
          //       freshStart: true,
          //     })
          //   }
          //   onOpenFullView={() => changeTab("today")}
          // />,
          ...(canOpenPortfolioSettings
            ? [
                <button
                  key="portfolio-settings"
                  type="button"
                  onClick={() => setPortfolioSettingsOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-gray-600 hover:border-gray-300 hover:text-gray-900"
                >
                  <SettingsIcon className="h-3.5 w-3.5" aria-hidden />
                  Portfolio Settings
                </button>,
              ]
            : []),
        ]}
      />
      <PortfolioSettingsModal
        isOpen={portfolioSettingsOpen}
        onClose={() => setPortfolioSettingsOpen(false)}
      />
      <div className="w-full bg-white p-3 md:p-4">
        <div className="min-h-[calc(100vh-8.9rem)] rounded-md bg-gray-100 p-4">
          <PortfolioSummaryStrip
            summary={summary}
            needsAttentionCriteria={overviewData?.needs_attention_criteria}
            activeTileFilter={tileFilter}
            onTileFilter={setTileFilter}
            onSwitchTab={() => {
              changeAccountPrioritiesView("list");
            }}
            fanOutUsed={summary.relationship_pairs_total}
            fanOutLimit={fanOutPortfolioLimit}
            maxFanIn={maxFanIn}
            fanInCap={fanInCap}
          />

          <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            {/* Tabs and the active tab's filters share the row; when they
                don't fit side by side the filters wrap to a row of their own
                instead of overlapping the tabs (S-61). */}
            <div className="flex flex-wrap items-center gap-3 overflow-visible bg-gray-50 px-4 py-3">
              <div className="min-w-0 max-w-full shrink-0 overflow-x-auto">
                <TabsComponent
                  tabs={mainTabs}
                  activeTab={activeTab}
                  onTabChange={id => changeTab(id as MainTab)}
                  variant="tab-segmented"
                  className="mb-0"
                />
              </div>
              <div
                ref={headerFiltersRef}
                className="flex min-w-0 max-w-full flex-auto items-center justify-end gap-2 overflow-visible"
              />
              {activeTab === "account_priorities" &&
                accountPrioritiesView === "maps" && (
                  <div
                    className="ml-auto hidden shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-0.5 text-[10.5px] text-gray-400 sm:flex"
                    aria-label="Portfolio Map terminology"
                  >
                    {LENS_KEY_TERMS.map(([abbr, term]) => (
                      <span
                        key={abbr}
                        className="inline-flex items-baseline gap-1 whitespace-nowrap"
                      >
                        <strong className="text-gray-500">{abbr}</strong>
                        <span>{term}</span>
                      </span>
                    ))}
                  </div>
                )}
            </div>

            <div className="min-h-[320px] bg-white p-4">
              {/* Today tab temporarily hidden.
              {activeTab === "today" && (
                <TodayTab
                  withOrg={withOrg}
                  onOpenAssess={account =>
                    void selfAssessment.openForAccount(account, {
                      freshStart: true,
                    })
                  }
                />
              )}
              */}

              {activeTab === "portfolio_goals" && (
                <PortfolioGoalsTab
                  accounts={accounts}
                  levelMix={levelMix}
                  loading={overviewLoading}
                  withOrg={withOrg}
                  headerFiltersRef={headerFiltersRef}
                  onOpenAssess={async account => {
                    const result = await selfAssessment.openForAccount(
                      account,
                      { freshStart: true }
                    );
                    if (result === "finalization" || result === "esl_locked") {
                      notifyPastDueLocked();
                    }
                  }}
                  onGoToAccounts={() => {
                    changeAccountPrioritiesView("list");
                  }}
                  onGoToRelationships={() => {
                    changeTab("relationship_priorities");
                  }}
                  workingSetAccountIds={workingSetAccountIds}
                  onClearWorkingSet={clearWorkingSet}
                />
              )}

              {activeTab === "actions" && (
                <ActionsTab
                  withOrg={withOrg}
                  onOpenAssess={async account => {
                    const result = await selfAssessment.openForAccount(
                      account,
                      { freshStart: true }
                    );
                    if (result === "finalization" || result === "esl_locked") {
                      notifyPastDueLocked();
                    }
                  }}
                  workingSetAccountIds={workingSetAccountIds}
                  onClearWorkingSet={clearWorkingSet}
                  headerFiltersRef={headerFiltersRef}
                  highlightAccountId={highlightAccountId}
                  highlightSignalKey={highlightSignalKey}
                  onHighlightConsumed={clearHighlight}
                />
              )}

              {activeTab === "account_priorities" && (
                <>
                  {/* Hold List|Maps + filter chrome until config resolves so
                      disabled layout never paints first when Maps are on. */}
                  {!mapsConfigResolved ? (
                    <div className="-mt-1 mb-4 h-9" aria-hidden />
                  ) : (
                    <div
                      className={`-mt-1 mb-4 flex min-w-0 flex-wrap items-center gap-2 sm:gap-4 ${
                        mapsTabVisible
                          ? "justify-between border-b border-gray-200"
                          : "justify-start"
                      }`}
                    >
                      {mapsTabVisible ? (
                        <div className="min-w-0 shrink overflow-x-auto">
                          <TabsComponent
                            tabs={accountPrioritiesSubTabs}
                            activeTab={accountPrioritiesView}
                            onTabChange={id =>
                              changeAccountPrioritiesView(
                                id as AccountPrioritiesView
                              )
                            }
                            variant="tab-underline"
                            className="mb-0 whitespace-nowrap"
                          />
                        </div>
                      ) : null}
                      {accountPrioritiesView === "maps" && mapsTabVisible && (
                        <div
                          ref={setMapsLensBarEl}
                          className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-3 overflow-x-auto py-1"
                        />
                      )}
                      {accountPrioritiesView === "list" && (
                        <div
                          className={
                            mapsTabVisible
                              ? "ml-auto flex min-w-0 shrink-0 flex-wrap items-center justify-end gap-2.5"
                              : "flex min-w-0 flex-wrap items-center justify-start gap-2.5"
                          }
                        >
                          <div
                            ref={setPrioritiesFilterBarEl}
                            className={
                              mapsTabVisible
                                ? "flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1.5"
                                : "flex min-w-0 flex-wrap items-center justify-start gap-x-3 gap-y-1.5"
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {accountPrioritiesView === "list" && (
                    <AccountPrioritiesTab
                      accounts={accounts}
                      pairs={pairs}
                      loading={overviewLoading}
                      withOrg={withOrg}
                      tileFilter={tileFilter}
                      onClearTileFilter={() => setTileFilter(null)}
                      headerFiltersRef={headerFiltersRef}
                      prioritiesFilterBarEl={
                        mapsConfigResolved ? prioritiesFilterBarEl : null
                      }
                      mapsConfigReady={mapsConfigResolved}
                      fanOutCap={fanOutCap}
                      onOpenAssess={async account => {
                        const result = await selfAssessment.openForAccount(
                          {
                            id: account.customer_id,
                            name: account.customer_name,
                          },
                          { freshStart: true }
                        );
                        if (
                          result === "finalization" ||
                          result === "esl_locked"
                        ) {
                          notifyPastDueLocked();
                        }
                      }}
                      workingSetAccountIds={workingSetAccountIds}
                      onClearWorkingSet={clearWorkingSet}
                      onOpenAccountOnMap={
                        mapsTabVisible ? openAccountOnMap : undefined
                      }
                      onOpenAccountsOnMap={
                        mapsTabVisible ? openAccountsOnMap : undefined
                      }
                      onGoToRelationshipPriorities={
                        goToRelationshipPrioritiesForAccount
                      }
                    />
                  )}

                  {accountPrioritiesView === "maps" && (
                    <PortfolioMapTab
                      onSeeAllActions={goToActionsWithHighlight}
                      withOrg={withOrg}
                      onOpenAssess={async account => {
                        const result = await selfAssessment.openForAccount(
                          account,
                          { freshStart: true }
                        );
                        if (
                          result === "finalization" ||
                          result === "esl_locked"
                        ) {
                          notifyPastDueLocked();
                        }
                      }}
                      overviewAccounts={accounts}
                      onGoToPriorities={subTab => {
                        if (subTab === "relationship") {
                          changeTab("relationship_priorities");
                        } else {
                          changeAccountPrioritiesView("list");
                        }
                      }}
                      onGoToPortfolioGoals={() => changeTab("portfolio_goals")}
                      onSetWorkingSet={setWorkingSetAccountIds}
                      workingSetAccountIds={workingSetAccountIds}
                      onClearWorkingSet={clearWorkingSet}
                      lensBarEl={mapsLensBarEl}
                      externalLensBar
                      focusAccountIds={mapsFocus?.accountIds ?? null}
                      focusHealthCategory={mapsFocus?.health ?? null}
                      onFocusConsumed={clearMapsFocus}
                    />
                  )}
                </>
              )}

              {activeTab === "relationship_priorities" && (
                <RelationshipPrioritiesTab
                  pairs={pairs}
                  accounts={accounts}
                  loading={overviewLoading}
                  lowScoreCriteria={overviewData?.low_score_criteria}
                  withOrg={withOrg}
                  headerFiltersRef={headerFiltersRef}
                  workingSetAccountIds={workingSetAccountIds}
                  onClearWorkingSet={clearWorkingSet}
                  focusAccountName={relPrioritiesFocus?.accountName ?? null}
                  focusAccountId={relPrioritiesFocus?.accountId ?? null}
                  suppressPairsFocusModal={
                    relPrioritiesFocus?.suppressPairsFocusModal ?? false
                  }
                  onFocusConsumed={clearRelPrioritiesFocus}
                  onGoBackToAccountPriorities={() =>
                    changeTab("account_priorities")
                  }
                />
              )}
            </div>
          </div>

          {/* Self-assessment modals (shared across tabs) */}
          <PortfolioAssessChoiceModal
            isOpen={selfAssessment.showChoiceModal}
            isLoading={
              selfAssessment.isValidationLoading &&
              selfAssessment.showChoiceModal
            }
            accountName={selfAssessment.accountName}
            latest={selfAssessment.latestSelfData}
            startingKind={
              selfAssessment.takingKind === "formal" ? "formal" : "team"
            }
            onClose={selfAssessment.resetModal}
            onContinue={selfAssessment.continueFromChoice}
          />
          <AssessmentSubmitSuccessModal
            info={selfAssessment.submitSuccessInfo}
            onClose={selfAssessment.closeSuccessModal}
            onBackToStatus={() => {
              selfAssessment.closeSuccessModal();
              navigate(withOrg("/pending-assessments?tab=status"));
            }}
            onViewMix={info => {
              selfAssessment.closeSuccessModal();
              navigate(
                withOrg(
                  `/pending-assessments?tab=level-matrix&mix=my&account=${info.accountId}`
                )
              );
            }}
          />
          <ReviewAssessmentModal
            isOpen={selfAssessment.showFormModal}
            templateData={selfAssessment.templateData}
            onClose={selfAssessment.resetModal}
            responses={selfAssessment.responses}
            onChange={selfAssessment.handleResponseChange}
            onSaveDraft={selfAssessment.saveDraft}
            onSubmit={selfAssessment.submit}
            isSubmitting={selfAssessment.isSubmitting}
            portfolioSubmitAction={selfAssessment.submitAction}
            voiceMode={selfAssessment.voiceMode}
            currentQuestionIndex={selfAssessment.questionIndex}
            onQuestionIndexChange={selfAssessment.setQuestionIndex}
            accountName={selfAssessment.accountName}
            footerVariant="portfolio"
            onGoToMyAssessments={selfAssessment.goToMyAssessments}
            canGoToMyAssessments={Boolean(
              selfAssessment.myAssessmentsNavTarget
            )}
            portfolioBlockedMessage={
              selfAssessment.formalAssessmentPendingMessage
            }
            isPortfolioValidationLoading={selfAssessment.isValidationLoading}
            onTakeFormalAssessment={
              selfAssessment.hasFormalAssessmentPending
                ? () => {
                    void (async () => {
                      const opened =
                        await selfAssessment.takeBlockingFormalAssessment();
                      if (opened) return;
                      void selfAssessment.goToMyAssessments();
                    })();
                  }
                : undefined
            }
            isTakeFormalAssessmentLoading={selfAssessment.isTakeFormalLoading}
            portfolioAssessMode={selfAssessment.assessMode}
            portfolioFormFlow={selfAssessment.portfolioFormFlow}
            carryForwardNote={selfAssessment.carryForwardNote}
            onCarryForwardNoteChange={selfAssessment.setCarryForwardNote}
            onFormBack={selfAssessment.backToChoice}
            portfolioStatusBadge={selfAssessment.portfolioStatusBadge}
            isFormalFoldIn={selfAssessment.isFormalFoldIn}
            isDraftSession={selfAssessment.isDraftSession}
            forceReadOnly={selfAssessment.forceReadOnly}
            teamPeerBanner={
              selfAssessment.takingKind === "team"
                ? selfAssessment.teamPeerBanner
                : null
            }
            reassessBanner={
              selfAssessment.forceReadOnly || selfAssessment.isDraftSession
                ? null
                : selfAssessment.reassessBanner
            }
            remainderBanner={
              selfAssessment.takingKind === "team"
                ? selfAssessment.privateRemainder
                : null
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MyPortfolioAccounts;
